import { c as _c } from "react/compiler-runtime";
import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import StandardModal from 'components/consumer/StandardModal';
import loadable from '@loadable/component';
import Placeholder from 'components/consumer/Placeholder';
import useScreen from 'hooks/useScreen';
import { useWindowSize } from 'react-use';
import convertImgixPropsToQueryParams from 'commons/convertImgixPropsToQueryParams';
import useVideoMetadata from 'hooks/useVideoMetadata';
import useIsVideoPosterPlayable from 'hooks/useIsVideoPosterPlayable';
import { getMainImageImgixPropsForPhoto } from '../PDPDesktopHeroGallery/helpers/imgix-props';
import { galleryCommonImgixProps, GALLERY_IMAGE_WIDTH, GALLERY_IMAGE_WIDTH_ZOOM } from './galleryCommonImgixProps';
const PDPDesktopFullScreenGallery2UIPlaceholder = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Placeholder loading={true} type="rect" style={{
      width: "100%",
      height: "70vh",
      margin: 0
    }} />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
const PDPDesktopFullScreenGallery2UI = loadable(() => import('./PDPDesktopFullScreenGallery2UI'), {
  fallback: <PDPDesktopFullScreenGallery2UIPlaceholder />,
  loading: PDPDesktopFullScreenGallery2UIPlaceholder
});
const PDPDesktopFullScreenGallery2 = ({
  isEditOrder = false,
  enableCrops = true,
  imageLocation,
  initialSelectedPhotoIndex = 0,
  introVideoId = null,
  isOpen = false,
  onClose,
  onDismiss,
  photos = [],
  productName = '',
  resetGalleryImageTracking,
  viewGalleryImageEvent
}) => {
  const {
    isMobileOrTablet
  } = useScreen();
  const {
    width: windowWidth
  } = useWindowSize();
  const galleryImageWidth = windowWidth >= 1640 ? 1800 : GALLERY_IMAGE_WIDTH;
  const [isVideoSelected, setVideoSelected] = useState(false);
  const [hasOpened, setHasOpened] = useState(isOpen);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(initialSelectedPhotoIndex);
  useEffect(() => {
    if (isOpen) {
      setHasOpened(true);
    }
  }, [isOpen]);
  const {
    thumbnail: videoThumbnailUrl,
    videoUrl: videoPosterUrl
  } = useVideoMetadata({
    videoId: isOpen || hasOpened ? introVideoId : null
  });
  const isPosterPlayable = useIsVideoPosterPlayable(videoPosterUrl);
  const videoIdToShow = videoThumbnailUrl || isPosterPlayable ? introVideoId : null;
  const getImageUrl = useCallback(({
    index,
    isZoomed
  }) => convertImgixPropsToQueryParams({
    ...getMainImageImgixPropsForPhoto(photos[index], true),
    ...galleryCommonImgixProps,
    width: isZoomed ? GALLERY_IMAGE_WIDTH_ZOOM : galleryImageWidth
  }, photos[index].src, isMobileOrTablet), [photos, isMobileOrTablet, galleryImageWidth]);
  const [photoSrc, setPhotoSrc] = useState(photos?.length && photos?.[initialSelectedPhotoIndex]?.src ? {
    zoomSrc: getImageUrl({
      index: initialSelectedPhotoIndex,
      isZoomed: true
    }),
    src: getImageUrl({
      index: initialSelectedPhotoIndex,
      isZoomed: false
    })
  } : '');
  const handleClose = () => {
    onClose();
  };

  // Close button, Escape or backdrop click. Choosing a fabric uses handleClose.
  const handleDismiss = () => {
    onDismiss?.();
    onClose();
  };
  const handleVideoSelected = useCallback(updatedIsVideoSelected => {
    if (updatedIsVideoSelected !== isVideoSelected) {
      setVideoSelected(updatedIsVideoSelected);
    }
  }, [isVideoSelected]);
  useEffect(() => {
    if (initialSelectedPhotoIndex !== selectedPhotoIndex) {
      setSelectedPhotoIndex(initialSelectedPhotoIndex);
    }
    // Open on the newly picked photo, not the video viewed last
    setVideoSelected(false);
    // Disable exhaustive dependencies rule here, because we only want the
    // selected photo to be updated by this hook if the
    // initialSelectedPhotoIndex (passed in via prop) is changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSelectedPhotoIndex]);

  // On open: restart the dedupe, then report the photo shown (none for video)
  const wasOpenRef = useRef(false);
  const lastInitialIndexRef = useRef(initialSelectedPhotoIndex);
  useEffect(() => {
    const isOpening = isOpen && !wasOpenRef.current;
    // The effect above applies a new opening photo, but state updates after
    // this commit. Otherwise the modal reopens on what was last viewed.
    const isNewOpeningPhoto = initialSelectedPhotoIndex !== lastInitialIndexRef.current;
    const shownIndex = isNewOpeningPhoto ? initialSelectedPhotoIndex : selectedPhotoIndex;
    const isVideoShown = !isNewOpeningPhoto && isVideoSelected;
    wasOpenRef.current = isOpen;
    lastInitialIndexRef.current = initialSelectedPhotoIndex;
    if (!isOpening) {
      return;
    }
    resetGalleryImageTracking?.();
    const photo = photos[shownIndex];
    if (!isVideoShown && photo) {
      viewGalleryImageEvent?.({
        imageLocation,
        imageFilename: photo.src?.split('/').pop() || '',
        position: shownIndex,
        totalImages: photos.length
      });
    }
  }, [imageLocation, initialSelectedPhotoIndex, isOpen, isVideoSelected, photos, resetGalleryImageTracking, selectedPhotoIndex, viewGalleryImageEvent]);
  useEffect(() => {
    if (!photos?.length || !photos?.[selectedPhotoIndex] || !isOpen) {
      return;
    }
    const updatedZoomSrc = getImageUrl({
      index: selectedPhotoIndex,
      isZoomed: true
    });
    const updatedSrc = getImageUrl({
      index: selectedPhotoIndex,
      isZoomed: false
    });
    if (updatedSrc !== photoSrc?.src) {
      setPhotoSrc({
        zoomSrc: updatedZoomSrc,
        src: updatedSrc
      });
    }
  }, [getImageUrl, isOpen, photos, photoSrc?.src, selectedPhotoIndex]);
  if (!photos?.length) {
    return <div className="PDPDesktopFullScreenGallery2__filler" />;
  }
  const shouldLoadUI = isOpen || hasOpened;
  return <StandardModal isOpen={isOpen} onClose={handleDismiss} theme="light" size="xl">
      {shouldLoadUI && <PDPDesktopFullScreenGallery2UI isEditOrder={isEditOrder} enableCrops={enableCrops} handleVideoSelected={handleVideoSelected} imageLocation={imageLocation} introVideoId={videoIdToShow} isOpen={isOpen} isVideoSelected={isVideoSelected} onClose={handleClose} photos={photos} photoSrc={photoSrc} productName={productName} selectedPhotoIndex={selectedPhotoIndex} setSelectedPhotoIndex={setSelectedPhotoIndex} videoPosterUrl={videoPosterUrl} videoThumbnailUrl={videoThumbnailUrl} viewGalleryImageEvent={viewGalleryImageEvent} />}
    </StandardModal>;
};
PDPDesktopFullScreenGallery2.propTypes = {
  isEditOrder: PropTypes.bool,
  enableCrops: PropTypes.bool,
  imageLocation: PropTypes.string,
  initialSelectedPhotoIndex: PropTypes.number,
  introVideoId: PropTypes.string,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  // User closed the modal (not by choosing a fabric)
  onDismiss: PropTypes.func,
  photos: PropTypes.arrayOf(PropTypes.shape({
    alt: PropTypes.string,
    src: PropTypes.string
  })),
  productName: PropTypes.string,
  resetGalleryImageTracking: PropTypes.func,
  viewGalleryImageEvent: PropTypes.func
};
export default PDPDesktopFullScreenGallery2;